import { useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  Activity,
  Wifi,
  WifiOff,
  LogOut,
  User,
  History,
  Home
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { 
  UploadArea, 
  VideoPlayer, 
  ProcessingProgress, 
  ResultsPanel,
  NotificationContainer,
  CalibrationPanel,
  VehicleConfigPanel,
  VideoHistory,
  HistoryDetail,
  Login,
  Register,
  ProtectedRoute
} from './components';
import { useVideoProcessing, useNotifications, useAPI } from './hooks';
import { useAuth } from './context/AuthContext';

// Navigation link component
function NavLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-500/20 text-primary-400'
          : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800'
      }`}
    >
      {icon}
      {label}
    </Link>
  );
}

function Dashboard() {
  const { 
    file, 
    status, 
    progress, 
    result,
    config,
    selectFile, 
    startProcessing, 
    cancelProcessing,
    resetProcessing,
    setConfig,
    isProcessing,
    isComplete,
    canProcess
  } = useVideoProcessing();

  const handleCalibrationComplete = (pixelsPerMeter: number) => {
    setConfig({ pixels_per_meter: pixelsPerMeter });
    // Mostrar notificación de éxito (notifications ya está disponible via useNotifications)
  };

  const { notifications, dismiss } = useNotifications();
  const { isConnected, isChecking } = useAPI();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Generate floating particles
  const particles = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 15,
      duration: 15 + Math.random() * 10
    }));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-dark-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 cyber-grid opacity-50" />
      
      {/* Gradient orbs */}
      <div className="fixed top-0 left-0 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />

      {/* Floating particles */}
      <div className="particles">
        {particles.map((p) => (
          <div
            key={p.id}
            className="particle"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-dark-800/50 backdrop-blur-lg shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-6"
            >
              <Link to="/" className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center neon-glow">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-950 animate-pulse" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">
                    VelocityAI
                  </h1>
                  <p className="text-xs text-dark-500 -mt-0.5">
                    Vehicle Speed Detection
                  </p>
                </div>
              </Link>
              
              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/" icon={<Home className="w-4 h-4" />} label="Inicio" />
                <NavLink to="/history" icon={<History className="w-4 h-4" />} label="Historial" />
              </nav>
            </motion.div>

            {/* Status indicators */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              {/* API Status */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-800/50 rounded-lg">
                {isChecking ? (
                  <Activity className="w-4 h-4 text-dark-400 animate-pulse" />
                ) : isConnected ? (
                  <Wifi className="w-4 h-4 text-green-400" />
                ) : (
                  <WifiOff className="w-4 h-4 text-red-400" />
                )}
                <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                  {isChecking ? 'Connecting...' : isConnected ? 'Connected' : 'Offline'}
                </span>
              </div>

              {/* User menu */}
              {user && (
                <div className="flex items-center gap-3 ml-2 pl-4 border-l border-dark-700">
                  <div className="flex items-center gap-2">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'User'} 
                        className="w-8 h-8 rounded-full border-2 border-primary-500/50"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="text-sm text-dark-300 hidden sm:block">
                      {user.displayName || user.email?.split('@')[0]}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-800 rounded-lg transition-colors"
                    title="Cerrar sesión"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {!isComplete ? (
            <motion.div
              key="upload-processing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Hero section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center max-w-3xl mx-auto mb-12"
              >
                <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                  <span className="gradient-text">AI-Powered</span>
                  <br />
                  Vehicle Speed Detection
                </h2>
                <p className="text-lg text-dark-400">
                  Upload traffic footage and let our AI detect vehicles
                  and calculate their speeds in real-time.
                </p>
              </motion.div>

              {/* Upload area */}
              <UploadArea 
                onFileSelect={selectFile}
                disabled={isProcessing}
              />

              {/* Video player preview */}
              {file && status === 'idle' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-8 space-y-4"
                >
                  <VideoPlayer file={file} />
                  
                  {/* Calibration panel */}
                  <CalibrationPanel
                    file={file}
                    pixelsPerMeter={config.pixels_per_meter}
                    onCalibrationComplete={handleCalibrationComplete}
                  />

                  {/* Vehicle detection config panel */}
                  <VehicleConfigPanel
                    selectedVehicles={config.vehicle_classes}
                    onVehicleChange={(vehicles) => setConfig({ vehicle_classes: vehicles })}
                    confidenceThreshold={config.confidence_threshold}
                    onConfidenceChange={(threshold) => setConfig({ confidence_threshold: threshold })}
                  />
                </motion.div>
              )}

              {/* Processing progress */}
              {(status !== 'idle' || file) && (
                <ProcessingProgress 
                  status={status}
                  progress={progress}
                  onCancel={isProcessing ? cancelProcessing : undefined}
                />
              )}

              {/* Start processing button */}
              {canProcess && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center"
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={startProcessing}
                    disabled={!isConnected}
                    className={`
                      relative overflow-hidden
                      px-8 py-4 rounded-xl
                      font-semibold text-lg
                      transition-all duration-300
                      ${isConnected 
                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white neon-glow-strong hover:shadow-lg hover:shadow-primary-500/30'
                        : 'bg-dark-700 text-dark-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Start AI Analysis
                    </span>
                    
                    {/* Shimmer effect */}
                    {isConnected && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                      />
                    )}
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ResultsPanel 
                result={result}
                onReset={resetProcessing}
              />

              {/* Video with detections */}
              {result && file && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-8"
                >
                  <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary-400" />
                    Video with Detections
                  </h3>
                  <VideoPlayer 
                    file={file}
                    processedVideoUrl={result.processed_video_url}
                    detections={result.detections}
                    vehicles={result.vehicles}
                  />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-auto border-t border-dark-800/50 shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-dark-500">
              <Zap className="w-4 h-4" />
              <span className="text-sm">
Powered by AI • VelocityAI
              </span>
            </div>
            <p className="text-sm text-dark-600">
              © 2026 VelocityAI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Notifications */}
      <NotificationContainer 
        notifications={notifications}
        onDismiss={dismiss}
      />
    </div>
  );
}

// History page wrapper with layout
function HistoryPage() {
  const { notifications, dismiss } = useNotifications();
  const { isConnected, isChecking } = useAPI();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 cyber-grid opacity-50" />
      <div className="fixed top-0 left-0 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />

      {/* Header */}
      <header className="relative z-10 border-b border-dark-800/50 backdrop-blur-lg shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-6"
            >
              <Link to="/" className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center neon-glow">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-950 animate-pulse" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">
                    VelocityAI
                  </h1>
                  <p className="text-xs text-dark-500 -mt-0.5">
                    Vehicle Speed Detection
                  </p>
                </div>
              </Link>
              
              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/" icon={<Home className="w-4 h-4" />} label="Inicio" />
                <NavLink to="/history" icon={<History className="w-4 h-4" />} label="Historial" />
              </nav>
            </motion.div>

            {/* Status indicators */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              {/* API Status */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-800/50 rounded-lg">
                {isChecking ? (
                  <Activity className="w-4 h-4 text-dark-400 animate-pulse" />
                ) : isConnected ? (
                  <Wifi className="w-4 h-4 text-green-400" />
                ) : (
                  <WifiOff className="w-4 h-4 text-red-400" />
                )}
                <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                  {isChecking ? 'Connecting...' : isConnected ? 'Connected' : 'Offline'}
                </span>
              </div>

              {/* User menu */}
              {user && (
                <div className="flex items-center gap-3 ml-2 pl-4 border-l border-dark-700">
                  <div className="flex items-center gap-2">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'User'} 
                        className="w-8 h-8 rounded-full border-2 border-primary-500/50"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="text-sm text-dark-300 hidden sm:block">
                      {user.displayName || user.email?.split('@')[0]}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-800 rounded-lg transition-colors"
                    title="Cerrar sesión"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <VideoHistory />
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-auto border-t border-dark-800/50 shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-dark-500">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Powered by AI • VelocityAI</span>
            </div>
            <p className="text-sm text-dark-600">© 2026 VelocityAI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Notifications */}
      <NotificationContainer notifications={notifications} onDismiss={dismiss} />
    </div>
  );
}

// History detail page wrapper
function HistoryDetailPage() {
  const { notifications, dismiss } = useNotifications();
  const { isConnected, isChecking } = useAPI();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 cyber-grid opacity-50" />
      <div className="fixed top-0 left-0 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />

      {/* Header */}
      <header className="relative z-10 border-b border-dark-800/50 backdrop-blur-lg shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-6"
            >
              <Link to="/" className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center neon-glow">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-950 animate-pulse" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">
                    VelocityAI
                  </h1>
                  <p className="text-xs text-dark-500 -mt-0.5">
                    Vehicle Speed Detection
                  </p>
                </div>
              </Link>
              
              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/" icon={<Home className="w-4 h-4" />} label="Inicio" />
                <NavLink to="/history" icon={<History className="w-4 h-4" />} label="Historial" />
              </nav>
            </motion.div>

            {/* Status indicators */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              {/* API Status */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-800/50 rounded-lg">
                {isChecking ? (
                  <Activity className="w-4 h-4 text-dark-400 animate-pulse" />
                ) : isConnected ? (
                  <Wifi className="w-4 h-4 text-green-400" />
                ) : (
                  <WifiOff className="w-4 h-4 text-red-400" />
                )}
                <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
                  {isChecking ? 'Connecting...' : isConnected ? 'Connected' : 'Offline'}
                </span>
              </div>

              {/* User menu */}
              {user && (
                <div className="flex items-center gap-3 ml-2 pl-4 border-l border-dark-700">
                  <div className="flex items-center gap-2">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'User'} 
                        className="w-8 h-8 rounded-full border-2 border-primary-500/50"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="text-sm text-dark-300 hidden sm:block">
                      {user.displayName || user.email?.split('@')[0]}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-800 rounded-lg transition-colors"
                    title="Cerrar sesión"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <HistoryDetail />
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-auto border-t border-dark-800/50 shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-dark-500">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Powered by AI • VelocityAI</span>
            </div>
            <p className="text-sm text-dark-600">© 2026 VelocityAI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Notifications */}
      <NotificationContainer notifications={notifications} onDismiss={dismiss} />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history/:jobId" 
        element={
          <ProtectedRoute>
            <HistoryDetailPage />
          </ProtectedRoute>
        } 
      />
      {/* Catch-all route - redirect to home */}
      <Route path="*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    </Routes>
  );
}

export default App;
